import { useMemo, useState } from "react";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button-shadcn";
import { UserAvatar } from "@/modern/components/user-avatar";
import { ArrowLeft, ChevronRight, GitBranch } from "lucide-react";
import type { EntityWidgetProps } from "@/other/constants/EntityManagerConfig";
import { cn } from "@/modern/utils";
import { EntityOwnershipProvider, useEntityOwnershipContext } from "./context/EntityOwnershipContext";
import { NotificationTypeRow } from "./components/NotificationTypeRow";
import {
  NOTIFY_CATEGORY_CONTENT,
  NOTIFY_CONNECTION_TYPES,
  PRIMARY_CONNECTION_TYPES,
} from "./constants/ownershipConstants";
import type { BoardUser } from "@/react/hooks/entityHooks/useEntityOwnership";

const ConnectionCard = ({
  type,
  boardUserIds,
  entityName,
  boardUsers,
  onSelectionChange,
}: {
  type: string;
  boardUserIds: number[];
  entityName: string;
  boardUsers: BoardUser[];
  onSelectionChange: (notifyType: string, userIds: number[]) => void;
}) => (
  <NotificationTypeRow
    notifyType={type}
    entityName={entityName}
    boardUserIds={boardUserIds}
    boardUsers={boardUsers}
    onSelectionChange={onSelectionChange}
  />
);

const NotifyCategoryCard = ({
  entityName,
  boardUsers,
  onOpen,
}: {
  entityName: string;
  boardUsers: BoardUser[];
  onOpen: () => void;
}) => {
  const config = NOTIFY_CATEGORY_CONTENT;
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "w-full text-left p-3.5 rounded-xl border border-border/70 bg-card/80",
        "shadow-sm shadow-black/5",
        "transition-[border-color,background-color] duration-150 ease-out",
        "hover:border-border hover:bg-accent/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "cursor-pointer",
      )}
      aria-label="Open notify connections"
    >
      <div className="flex items-start gap-3">
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-lg shadow-sm"
          style={{ backgroundColor: config.iconBg }}
          aria-hidden
        >
          <Icon className="size-4 text-white" strokeWidth={2} />
        </span>

        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex flex-col gap-0.5">
              <p className="text-base font-medium text-foreground leading-snug tracking-tight">
                {config.title}
              </p>
              <p className="text-sm text-muted-foreground leading-snug">
                {config.description(entityName)}
              </p>
            </div>
            <span
              className="flex size-8 shrink-0 items-center justify-center text-muted-foreground"
              aria-hidden
            >
              <ChevronRight className="size-3.5" />
            </span>
          </div>

          <div className="flex items-center gap-2.5 min-h-7">
            {boardUsers.length > 0 ? (
              <>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {config.usersLabel}
                </span>
                <div className="flex items-center -space-x-1.5">
                  {boardUsers.slice(0, 3).map((user) => (
                    <UserAvatar key={user.id} user={user} size="small" showTooltip={true} showRemove={false} />
                  ))}
                  {boardUsers.length > 3 ? (
                    <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground ring-2 ring-background">
                      +{boardUsers.length - 3}
                    </span>
                  ) : null}
                </div>
              </>
            ) : (
              <span className="text-sm text-muted-foreground italic">No users connected</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const EntityOwnershipViewContent = ({ onClose, headerTitle }: { onClose?: () => void; headerTitle?: string }) => {
  const { ownershipData, boardUsers, handleUserSelectionChange, entityName, entityRecord } =
    useEntityOwnershipContext();
  const [showNotifyDetail, setShowNotifyDetail] = useState(false);

  const isResource = entityName === "resource" || entityRecord.entityType === "resourceId";

  const byType = useMemo(() => new Map(ownershipData.map((row) => [row.type, row])), [ownershipData]);

  const primaryRows = useMemo(
    () => PRIMARY_CONNECTION_TYPES.map((type) => byType.get(type)).filter(Boolean) as typeof ownershipData,
    [byType, ownershipData],
  );
  const notifyRows = useMemo(
    () => NOTIFY_CONNECTION_TYPES.map((type) => byType.get(type)).filter(Boolean) as typeof ownershipData,
    [byType, ownershipData],
  );

  const notifyUsers = useMemo(() => {
    const ids = new Set<number>();
    for (const row of notifyRows) {
      for (const id of row.boardUserIds) ids.add(id);
    }
    return [...ids]
      .map((id) => boardUsers.find((u) => u.id === id))
      .filter(Boolean) as BoardUser[];
  }, [notifyRows, boardUsers]);

  const isEmpty = ownershipData.length === 0;
  const inNotifyDrillIn = isResource && showNotifyDetail;

  const entityPrefix = headerTitle?.includes(" | ")
    ? headerTitle.slice(0, headerTitle.indexOf(" | "))
    : undefined;
  const detailLabel = "Notify";
  const title = inNotifyDrillIn
    ? entityPrefix
      ? `${entityPrefix} | ${detailLabel}`
      : detailLabel
    : (headerTitle ?? "Connections");

  return (
    <DashboardCard
      onClose={onClose}
      cardContentClassName="p-0 h-full flex flex-col min-h-0"
      header={
        <div className="flex items-center justify-between w-full">
          <CardTitle className="text-base gap-3 flex items-center truncate min-w-0 font-normal tracking-tight">
            {inNotifyDrillIn ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-7 p-0 text-muted-foreground"
                onClick={() => setShowNotifyDetail(false)}
                aria-label="Back to connections"
              >
                <ArrowLeft className="size-4" />
              </Button>
            ) : null}
            <span className="truncate min-w-0 pr-2">{title}</span>
          </CardTitle>
        </div>
      }
    >
      {isEmpty ? (
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="min-h-[12rem] w-full flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border/60 bg-muted/30 p-5 text-center">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <GitBranch className="size-5" aria-hidden />
            </span>
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-foreground">No connections</h3>
              <p className="text-[0.8125rem] text-muted-foreground leading-snug">
                Connection settings are not available for this record.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="flex flex-col gap-3 p-3.5 pb-5">
            {isResource && !inNotifyDrillIn ? (
              <div className="flex flex-col gap-2.5">
                {primaryRows.map((data) => (
                  <ConnectionCard
                    key={data.type}
                    type={data.type}
                    boardUserIds={data.boardUserIds}
                    entityName={entityName}
                    boardUsers={boardUsers}
                    onSelectionChange={handleUserSelectionChange}
                  />
                ))}
                {notifyRows.length > 0 ? (
                  <NotifyCategoryCard
                    entityName={entityName}
                    boardUsers={notifyUsers}
                    onOpen={() => setShowNotifyDetail(true)}
                  />
                ) : null}
              </div>
            ) : null}

            {(!isResource || inNotifyDrillIn) && notifyRows.length > 0 ? (
              <div className="flex flex-col gap-2.5">
                {notifyRows.map((data) => (
                  <ConnectionCard
                    key={data.type}
                    type={data.type}
                    boardUserIds={data.boardUserIds}
                    entityName={entityName}
                    boardUsers={boardUsers}
                    onSelectionChange={handleUserSelectionChange}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </DashboardCard>
  );
};

export const EntityOwnershipView = ({ record, onClose, headerTitle }: EntityWidgetProps) => {
  const resolvedTitle = !headerTitle || headerTitle === "Ownership" ? "Connections" : headerTitle;

  return (
    <EntityOwnershipProvider record={record}>
      <EntityOwnershipViewContent onClose={onClose} headerTitle={resolvedTitle} />
    </EntityOwnershipProvider>
  );
};
